'use strict'

/**
 * RGBColor
 * Workflow: store RGB components, provide helper conversions and formatting.
 */
class RGBColor {
    /**
     * @param {number|Array<number>} r R component or [r, g, b] array or packed int.
     * @param {number} g G component.
     * @param {number} b B component.
     */
    constructor(r = 0.0, g = 0.0, b = 0.0) {
        if (Array.isArray(r)) {
            this.r = r[0]
            this.g = r[1]
            this.b = r[2]
            return
        }

        if (typeof r === 'number' && g === undefined && b === undefined) {
            const value = Number(r) || 0
            this.r = (value >> 16) & 0xff
            this.g = (value >> 8) & 0xff
            this.b = value & 0xff
            return
        }

        this.r = r
        this.g = g
        this.b = b
    }

    /**
     * Read the channel value for the given enum.
     * @param {{value:number}} channel
     * @returns {number}
     */
    channelValue(channel) {
        if (channel === RGBColor.Channel.R) return this.r
        return channel === RGBColor.Channel.G ? this.g : this.b
    }

    /**
     * Return RGB components as a numeric array.
     * @returns {number[]}
     */
    getRGBColorAsDoubles() {
        return [this.r, this.g, this.b]
    }

    /**
     * Return normalized RGB components (0..1).
     * @returns {number[]}
     */
    getNormalizedRGBColorAsDoubles() {
        return [this.r / 255.0, this.g / 255.0, this.b / 255.0]
    }

    /**
     * Return packed RGB integer.
     * @returns {number}
     */
    getRGBColorInt() {
        const r = RGBColor.inRGBRange(Math.round(this.r))
        const g = RGBColor.inRGBRange(Math.round(this.g))
        const b = RGBColor.inRGBRange(Math.round(this.b))
        return (r << 16) | (g << 8) | b
    }

    /**
     * Return hex string in #RRGGBB format.
     * @returns {string}
     */
    getWebHexValue() {
        const color = this.getRGBColorInt() & 0xffffff
        return `#${color.toString(16).toUpperCase().padStart(6, '0')}`
    }

    /**
     * Clamp a channel to 0..255.
     * @param {number} value
     * @returns {number}
     */
    static inRGBRange(value) {
        return Math.max(0.0, Math.min(255.0, value))
    }

    /**
     * Human-readable string representation.
     * @returns {string}
     */
    toString() {
        const r = Number(this.r).toFixed(2)
        const g = Number(this.g).toFixed(2)
        const b = Number(this.b).toFixed(2)
        return `RGBColor - R=${r} - G=${g} - B=${b}`
    }
}

// Factory for enum-style channels with methods (mirrors Android API names).
const makeChannel = (value, chanelName) =>
    Object.freeze({
        value,
        chanelName,
        getChanelName() {
            return this.chanelName
        },
        getValue() {
            return this.value
        },
    })

// Enum-like list of RGB channels.
RGBColor.Channel = Object.freeze({
    R: makeChannel(0, 'Red'),
    G: makeChannel(1, 'Green'),
    B: makeChannel(2, 'Blue'),
})

export default RGBColor
